import { useState } from 'react';
import { Search, Filter, Zap, Tag, CheckSquare, Square, X, Plus } from 'lucide-react';
import Avatar from '../ui/Avatar';
import Badge from '../ui/Badge';
import { CONVERSATION_STATUS, CONVERSATION_STATUS_LABELS } from '../../data/conversations';

const CHANNELS = [
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'web', label: 'Web' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'email', label: 'E-mail' },
  { id: 'outros', label: 'Outros' },
];

export default function ConversationList({
  conversations,
  selectedId,
  onSelect,
  teams = {},
  funnelStages = [],
  statusFilter = [],
  onStatusFilterChange,
  searchQuery = '',
  onSearchQueryChange,
  teamFilterId = null,
  onTeamFilterIdChange,
  channelFilter = null,
  onChannelFilterChange,
  tagFilterId = null,
  onTagFilterIdChange,
  tags = [],
  onNewConversation,
}) {
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  const teamsList = Array.isArray(teams) ? teams : Object.values(teams);

  const getStage = (stageId) => {
    const value = stageId || 'triagem';
    return (
      funnelStages.find((s) => s.id === value || s.slug === value) || {
        id: 'triagem',
        label: 'Triagem',
        headerColor: 'bg-slate-100 text-slate-800',
        border: 'border-slate-200',
      }
    );
  };

  const statusArr = Array.isArray(statusFilter) ? statusFilter : [];
  const hasActiveFilters = statusArr.length > 0 || teamFilterId || channelFilter || searchQuery.trim();
  const [tagFilterOpen, setTagFilterOpen] = useState(false);

  return (
    <div className="w-[320px] lg:w-[380px] bg-appSurface border-r border-appSurfaceBorder flex flex-col h-full shrink-0">
      <div className="px-4 pt-4 pb-3 border-b border-appSurfaceBorder shrink-0 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-bold text-xl text-appText tracking-tight shrink-0">Mensagens</h2>
          {onNewConversation && (
            <button
              type="button"
              onClick={onNewConversation}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-appPrimary text-white text-sm font-bold hover:opacity-90 shrink-0"
              aria-label="Nova conversa"
            >
              <Plus size={16} />
              <span className="hidden sm:inline">Nova conversa</span>
            </button>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <div
            className={`flex items-center gap-2 rounded-lg transition-all overflow-hidden flex-1 min-w-0 ${
              searchFocused ? 'bg-appSurfaceBorder/30' : ''
            }`}
          >
            <label className="flex items-center gap-2 flex-1 min-w-0 cursor-text py-1">
              <Search size={18} className="text-appTextMuted shrink-0" />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => onSearchQueryChange?.(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder="Buscar..."
                className="bg-transparent border-none outline-none text-sm text-appText placeholder:text-appTextMuted w-full min-w-0 max-w-[100px] lg:max-w-[140px] py-1"
              />
            </label>
            {searchQuery && (
              <button
                type="button"
                onClick={() => onSearchQueryChange?.('')}
                className="p-1 text-appTextMuted hover:text-appText rounded shrink-0"
                aria-label="Limpar busca"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-0.5 shrink-0 ml-auto">
              {tags.length > 0 && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => { setTagFilterOpen((v) => !v); setFilterMenuOpen(false); }}
                    className={`p-2 rounded-lg transition-colors ${tagFilterOpen || tagFilterId ? 'bg-appPrimary/20 text-appPrimary' : 'text-appTextMuted hover:bg-appSurfaceBorder/30'}`}
                    aria-label="Filtrar por etiqueta"
                  >
                    <Tag size={18} />
                  </button>
                  {tagFilterOpen && (
                    <>
                      <div className="fixed inset-0 z-10" aria-hidden onClick={() => setTagFilterOpen(false)} />
                      <div className="absolute right-0 top-full mt-1 z-20 w-56 bg-appSurface border border-appSurfaceBorder rounded-xl shadow-lg py-3 px-3 max-h-[70vh] overflow-y-auto">
                        <p className="text-[10px] font-bold text-appTextMuted uppercase tracking-widest px-1 mb-2">Etiquetas</p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => { onTagFilterIdChange?.(null); setTagFilterOpen(false); }}
                            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${!tagFilterId ? 'bg-appPrimary/20 text-appPrimary ring-1 ring-appPrimary/40' : 'bg-appSurfaceBorder/30 text-appTextMuted hover:bg-appSurfaceBorder/50'}`}
                          >
                            Todas
                          </button>
                          {tags.map((t) => (
                            <button
                              key={t.id}
                              type="button"
                              onClick={() => { onTagFilterIdChange?.(tagFilterId === t.id ? null : t.id); setTagFilterOpen(false); }}
                              className="focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-appPrimary rounded-full"
                            >
                              <Badge
                                colorClass={`${t.color || 'bg-appSurfaceBorder text-appText'} border ${t.border || 'border-transparent'} ${tagFilterId === t.id ? 'ring-2 ring-appPrimary ring-offset-1' : ''}`}
                              >
                                {t.name}
                              </Badge>
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => { setFilterMenuOpen((v) => !v); setTagFilterOpen(false); }}
                  className={`p-2 rounded-lg transition-colors ${filterMenuOpen || hasActiveFilters ? 'bg-appPrimary/20 text-appPrimary' : 'text-appTextMuted hover:bg-appSurfaceBorder/30'}`}
                  aria-label="Filtrar"
                >
                  <Filter size={18} />
                </button>
              {filterMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" aria-hidden onClick={() => setFilterMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-20 w-56 bg-appSurface border border-appSurfaceBorder rounded-xl shadow-lg py-3 px-2 max-h-[70vh] overflow-y-auto">
                    <p className="text-[10px] font-bold text-appTextMuted uppercase tracking-widest px-3 mb-2">Filtros</p>
                    <p className="text-[10px] font-bold text-appTextMuted uppercase tracking-widest px-3 mb-1.5">Status da conversa</p>
                    {[CONVERSATION_STATUS.ATIVO, CONVERSATION_STATUS.AGUARDANDO, CONVERSATION_STATUS.FINALIZADO].map((value) => {
                      const isSelected = statusArr.includes(value);
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            const next = isSelected ? statusArr.filter((s) => s !== value) : [...statusArr, value];
                            onStatusFilterChange?.(next);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-appSurfaceBorder/20 text-left text-sm ${isSelected ? 'bg-appPrimary/10 text-appPrimary font-medium' : 'text-appText'}`}
                        >
                          {isSelected ? <CheckSquare size={16} className="shrink-0" /> : <Square size={16} className="shrink-0 text-appTextMuted" />}
                          {CONVERSATION_STATUS_LABELS[value]}
                        </button>
                      );
                    })}
                    {teamsList.length > 0 && (
                      <>
                        <div className="border-t border-appSurfaceBorder my-2" />
                        <p className="text-[10px] font-bold text-appTextMuted uppercase tracking-widest px-3 mb-1.5">Equipe</p>
                        <button
                          type="button"
                          onClick={() => { onTeamFilterIdChange?.(null); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-appSurfaceBorder/20 text-left text-sm ${!teamFilterId ? 'bg-appPrimary/10 text-appPrimary font-medium' : 'text-appText'}`}
                        >
                          {!teamFilterId ? <CheckSquare size={16} className="shrink-0" /> : <Square size={16} className="shrink-0 text-appTextMuted" />}
                          Todas
                        </button>
                        {teamsList.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => { onTeamFilterIdChange?.(t.id); }}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-appSurfaceBorder/20 text-left text-sm ${teamFilterId === t.id ? 'bg-appPrimary/10 text-appPrimary font-medium' : 'text-appText'}`}
                          >
                            {teamFilterId === t.id ? <CheckSquare size={16} className="shrink-0" /> : <Square size={16} className="shrink-0 text-appTextMuted" />}
                            {t.name}
                          </button>
                        ))}
                      </>
                    )}
                    <div className="border-t border-appSurfaceBorder my-2" />
                    <p className="text-[10px] font-bold text-appTextMuted uppercase tracking-widest px-3 mb-1.5">Canal de venda</p>
                    <button
                      type="button"
                      onClick={() => { onChannelFilterChange?.(null); }}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-appSurfaceBorder/20 text-left text-sm ${!channelFilter ? 'bg-appPrimary/10 text-appPrimary font-medium' : 'text-appText'}`}
                    >
                      {!channelFilter ? <CheckSquare size={16} className="shrink-0" /> : <Square size={16} className="shrink-0 text-appTextMuted" />}
                      Todos
                    </button>
                    {CHANNELS.map((ch) => (
                      <button
                        key={ch.id}
                        type="button"
                        onClick={() => { onChannelFilterChange?.(ch.id); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-appSurfaceBorder/20 text-left text-sm ${channelFilter === ch.id ? 'bg-appPrimary/10 text-appPrimary font-medium' : 'text-appText'}`}
                      >
                        {channelFilter === ch.id ? <CheckSquare size={16} className="shrink-0" /> : <Square size={16} className="shrink-0 text-appTextMuted" />}
                        {ch.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
              </div>
            </div>
          </div>
        </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {conversations.map(conv => {
          const stage = getStage(conv.stage);
          return (
            <div
              key={conv.id}
              onClick={() => onSelect(conv.id)}
              className={`flex items-center px-5 py-4 cursor-pointer transition-all border-l-4 ${selectedId === conv.id ? 'bg-appSurfaceBorder/20 border-appPrimary' : 'hover:bg-appSurfaceBorder/10 border-transparent'}`}
              role="button"
              tabIndex={0}
            >
              <Avatar src={conv.avatarUrl} label={conv.avatarLabel ?? conv.avatar} size="lg" />
              <div className="ml-4 flex-1 min-w-0">
                <div className="flex justify-between items-baseline mb-1">
                  <span className="font-bold text-sm text-appText truncate">{conv.name}</span>
                  <span className="text-[10px] text-appTextMuted font-medium">{conv.timestamp}</span>
                </div>
                <p className="text-xs text-appTextMuted truncate mb-2">{conv.lastMessage}</p>
                <div className="flex flex-wrap gap-2 items-center">
                  {funnelStages.length > 0 && (
                    <Badge colorClass={`${stage.headerColor} border ${stage.border || 'border-transparent'} flex items-center gap-1.5 font-medium`}>
                      <Filter size={12} className="shrink-0 opacity-80" />
                      {stage.label}
                    </Badge>
                  )}
                  {conv.aiActive && (
                    <Badge colorClass="bg-appPrimaryLight text-appPrimaryText border-appPrimary/30 flex items-center gap-1">
                      <Zap size={8} /> IA Ativa
                    </Badge>
                  )}
                </div>
              </div>
              {conv.unread > 0 && (
                <div className="ml-2 w-5 h-5 bg-appPrimary text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                  {conv.unread}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
